import '@theme/theme-scroller';
import { getScrollContainer } from '@theme/scroll-container';

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

class ThemeCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-theme-track]');
    this.slides = [...this.querySelectorAll('[data-theme-slide]')];
    this.dots = [...this.querySelectorAll('[data-theme-dot]')];
    if (!this.track || this.slides.length < 2) return;
    this.index = Math.max(0, this.slides.findIndex((slide) => !slide.hidden));
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.hovered = false;
    if (this.hasAttribute('data-swipe')) this.setupSwipeNavigation(signal);
    this.querySelector('[data-theme-prev]')?.addEventListener('click', () => this.show(this.index - 1), { signal });
    this.querySelector('[data-theme-next]')?.addEventListener('click', () => this.show(this.index + 1), { signal });
    this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.show(index), { signal }));
    this.addEventListener('mouseenter', () => { this.hovered = true; this.stop(); }, { signal });
    this.addEventListener('mouseleave', () => { this.hovered = false; this.start(); }, { signal });
    this.addEventListener('focusin', () => this.stop(), { signal });
    this.addEventListener('focusout', (event) => {
      if (!this.contains(event.relatedTarget)) queueMicrotask(() => this.start());
    }, { signal });
    reduceMotion.addEventListener('change', () => {
      if (reduceMotion.matches) this.finishTransition?.();
      this.start();
    }, { signal });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.finishTransition?.();
      this.start();
    }, { signal });
    window.addEventListener('resize', () => {
      this.finishTransition?.();
      this.updateControlsColor();
    }, { signal });
    this.addEventListener('shopify:block:select', (event) => {
      const slide = event.target.closest('[data-theme-slide]');
      const index = this.slides.indexOf(slide);
      if (index < 0) return;
      this.editorSelected = true;
      this.show(index, false);
      this.stop();
    }, { signal });
    this.addEventListener('shopify:block:deselect', () => {
      this.editorSelected = false;
      this.start();
    }, { signal });
    this.show(this.index, false);
    this.start();
  }

  setupSwipeNavigation(signal) {
    this.mobile = window.matchMedia('(max-width: 749px)');
    let swipe;
    this.mobile.addEventListener('change', () => {
      swipe = null;
      this.start();
    }, { signal });
    this.track.addEventListener('pointerdown', (event) => {
      if (!event.isPrimary) {
        swipe = null;
        return;
      }
      if (event.button !== 0) return;
      // The comparison handle and other controls keep their native interactions.
      if (event.target.closest('input, button, a, select, textarea')) return;
      swipe = { id: event.pointerId, x: event.clientX, y: event.clientY };
      this.track.setPointerCapture(event.pointerId);
      event.preventDefault();
    }, { signal });
    this.track.addEventListener('pointermove', (event) => {
      if (!swipe || event.pointerId !== swipe.id) return;
      const x = Math.abs(event.clientX - swipe.x);
      const y = Math.abs(event.clientY - swipe.y);
      // Let a vertical gesture scroll the page without changing slides on release.
      if (y > 10 && y > x) swipe = null;
    }, { signal });
    this.track.addEventListener('pointerup', (event) => {
      if (!swipe || event.pointerId !== swipe.id) return;
      const x = event.clientX - swipe.x;
      const y = event.clientY - swipe.y;
      swipe = null;
      if (Math.abs(x) >= 40 && Math.abs(x) > Math.abs(y) * 1.5) {
        this.show(this.index + (x < 0 ? 1 : -1));
      }
    }, { signal });
    ['pointercancel', 'lostpointercapture'].forEach((type) => {
      this.track.addEventListener(type, () => { swipe = null; }, { signal });
    });
  }

  show(index, animate = true) {
    // Finish an interrupted move before starting another, so rapid clicks cannot
    // leave stale slides visible or let an old completion hide the current slide.
    this.finishTransition?.();
    const previous = this.index;
    const direction = index < previous ? -1 : 1;
    const outgoing = this.slides[previous];
    this.index = (index + this.slides.length) % this.slides.length;
    const incoming = this.slides[this.index];
    const shouldAnimate = animate && !reduceMotion.matches && previous !== this.index;
    const height = shouldAnimate ? this.track.getBoundingClientRect().height : 0;
    const transition = shouldAnimate && height > 0;
    this.slides.forEach((slide, slideIndex) => {
      const active = slideIndex === this.index;
      slide.hidden = !active && !(transition && slide === outgoing);
      slide.inert = !active;
      slide.setAttribute('aria-hidden', String(!active));
    });
    this.dots.forEach((dot, dotIndex) => dot.setAttribute('aria-current', String(dotIndex === this.index)));
    this.updateControlsColor();

    if (transition) {
      this.track.classList.add('theme-carousel-track--moving');
      outgoing.classList.add('theme-carousel-slide--outgoing');
      const nextHeight = this.track.getBoundingClientRect().height;
      const timing = { duration: 450, easing: 'cubic-bezier(.22, 1, .36, 1)' };
      const animations = [
        outgoing.animate([{ transform: 'translateX(0)' }, { transform: `translateX(${-direction * 100}%)` }], timing),
        incoming.animate([{ transform: `translateX(${direction * 100}%)` }, { transform: 'translateX(0)' }], timing),
        this.track.animate([{ height: `${height}px` }, { height: `${nextHeight}px` }], timing),
      ];
      const finish = () => {
        if (this.finishTransition !== finish) return;
        this.finishTransition = null;
        outgoing.hidden = true;
        outgoing.classList.remove('theme-carousel-slide--outgoing');
        this.track.classList.remove('theme-carousel-track--moving');
        animations.forEach((animation) => animation.cancel());
        // The incoming video may have been outside the viewport at transition start.
        this.dispatchEvent(new CustomEvent('theme:slide-change', { bubbles: true, detail: { index: this.index } }));
      };
      this.finishTransition = finish;
      Promise.all(animations.map((animation) => animation.finished)).then(finish, () => {});
    }
    this.dispatchEvent(new CustomEvent('theme:slide-change', { bubbles: true, detail: { index: this.index } }));
  }

  updateControlsColor() {
    if (!this.classList.contains('media-hero')) return;
    const slide = this.slides[this.index];
    const mobileColor = window.matchMedia('(max-width: 749px)').matches ? slide.dataset.themeMobileControlsColor : '';
    const color = mobileColor || slide.dataset.themeControlsColor;
    if (color) this.style.setProperty('--media-hero-controls-color', color);
    else this.style.removeProperty('--media-hero-controls-color');
  }

  start() {
    this.stop();
    const autoplay = this.mobile?.matches ? this.hasAttribute('data-mobile-autoplay') : this.hasAttribute('autoplay');
    if (!this.isConnected || !autoplay || reduceMotion.matches || document.hidden || this.hovered || this.editorSelected || this.contains(document.activeElement)) return;
    const delay = Number(this.dataset.interval || 7000);
    this.timer = window.setInterval(() => this.show(this.index + 1), delay);
  }

  stop() {
    window.clearInterval(this.timer);
  }

  disconnectedCallback() {
    this.stop();
    this.finishTransition?.();
    this.controller?.abort();
  }
}

class ThemeTabs extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.panels = [...this.querySelectorAll('[role="tabpanel"]')];
    this.tabs.forEach((tab, index) => tab.addEventListener('click', () => this.show(index), { signal }));
    this.tabs.forEach((tab, index) => tab.addEventListener('keydown', (event) => this.handleKeydown(event, index), { signal }));
    this.addEventListener('shopify:block:select', (event) => {
      const panel = event.target.closest('[role="tabpanel"]');
      const index = this.panels.indexOf(panel);
      if (index >= 0) this.show(index);
    }, { signal });
    this.show(Math.max(0, this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true')));
  }

  show(index) {
    this.tabs.forEach((tab, tabIndex) => {
      tab.setAttribute('aria-selected', String(tabIndex === index));
      tab.tabIndex = tabIndex === index ? 0 : -1;
    });
    this.panels.forEach((panel, panelIndex) => (panel.hidden = panelIndex !== index));
    this.dispatchEvent(new CustomEvent('theme:slide-change', { bubbles: true }));
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  handleKeydown(event, index) {
    let nextIndex;
    if (event.key === 'ArrowLeft') nextIndex = (index - 1 + this.tabs.length) % this.tabs.length;
    if (event.key === 'ArrowRight') nextIndex = (index + 1) % this.tabs.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = this.tabs.length - 1;
    if (nextIndex === undefined) return;
    event.preventDefault();
    this.show(nextIndex);
    this.tabs[nextIndex].focus();
    if (this.tabs[nextIndex].closest('.theme-product-tabs__tabs')) {
      this.tabs[nextIndex].scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }
}

class ThemeAccordion extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.finishAnimations();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.items = [...this.querySelectorAll('details')];
    this.animationStates = new Map();
    this.items.forEach((item) => {
      if (this.hasAttribute('data-animate')) {
        this.animationStates.set(item, {
          open: item.open,
          height: item.style.height,
          overflow: item.style.overflow,
          boxSizing: item.style.boxSizing,
          animation: null,
        });
        item.querySelector('summary')?.addEventListener('click', (event) => {
          event.preventDefault();
          const state = this.animationStates.get(item);
          if (state.open) this.setOpen(item, false);
          else this.open(item);
        }, { signal });
      }
      item.addEventListener('toggle', () => {
        const state = this.animationStates.get(item);
        if (state?.animation) return;
        if (state) state.open = item.open;
        if (item.open) this.open(item);
      }, { signal });
    });
    if (this.hasAttribute('data-animate')) {
      reduceMotion.addEventListener('change', () => this.finishAnimations(), { signal });
      window.addEventListener('resize', () => this.finishAnimations(), { signal });
      this.addEventListener('wheel', () => this.stopScrollAnchor(), { passive: true, signal });
      this.addEventListener('touchmove', () => this.stopScrollAnchor(), { passive: true, signal });
    }
    this.addEventListener('shopify:block:select', (event) => {
      const item = event.target.closest('details');
      if (this.items.includes(item)) this.open(item, false);
    }, { signal });
  }

  open(selected, animate = true) {
    this.stopScrollAnchor();
    const summary = selected.querySelector('summary');
    const top = summary?.getBoundingClientRect().top;
    this.items.forEach((item) => this.setOpen(item, item === selected, animate));
    if (!animate || !summary || !this.hasAttribute('data-preserve-scroll')) return;

    const container = getScrollContainer();
    const preservePosition = () => {
      const offset = summary.getBoundingClientRect().top - top;
      if (Math.abs(offset) > 0.5) {
        container.scrollTo({ top: container.scrollTop + offset, behavior: 'instant' });
      }
      this.scrollAnchorFrame = [...this.animationStates.values()].some((state) => state.animation)
        ? requestAnimationFrame(preservePosition)
        : null;
    };
    preservePosition();
  }

  stopScrollAnchor() {
    if (this.scrollAnchorFrame != null) cancelAnimationFrame(this.scrollAnchorFrame);
    this.scrollAnchorFrame = null;
  }

  setOpen(item, open, animate = true) {
    const state = this.animationStates.get(item);
    if (!state) {
      item.open = open;
      return;
    }
    if (state.open === open && animate) return;

    const startHeight = item.getBoundingClientRect().height;
    if (!state.animation) {
      state.height = item.style.height;
      state.overflow = item.style.overflow;
      state.boxSizing = item.style.boxSizing;
    }
    state.animation?.cancel();
    state.animation = null;
    state.open = open;
    this.restoreStyles(item, state);
    item.open = open;
    const endHeight = item.getBoundingClientRect().height;

    if (!animate || reduceMotion.matches || !item.animate || Math.abs(startHeight - endHeight) < 1) return;

    item.open = true;
    item.style.boxSizing = 'border-box';
    item.style.height = `${startHeight}px`;
    item.style.overflow = 'hidden';
    const animation = item.animate(
      { height: [`${startHeight}px`, `${endHeight}px`] },
      { duration: 280, easing: 'cubic-bezier(0.25, 0.1, 0.25, 1)', fill: 'both' },
    );
    state.animation = animation;
    animation.onfinish = () => {
      if (state.animation !== animation) return;
      state.animation = null;
      item.open = state.open;
      this.restoreStyles(item, state);
      animation.cancel();
    };
  }

  restoreStyles(item, state) {
    item.style.height = state.height;
    item.style.overflow = state.overflow;
    item.style.boxSizing = state.boxSizing;
  }

  finishAnimations() {
    this.stopScrollAnchor();
    this.animationStates?.forEach((state, item) => {
      if (!state.animation) return;
      state.animation.cancel();
      state.animation = null;
      item.open = state.open;
      this.restoreStyles(item, state);
    });
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.finishAnimations();
    this.animationStates?.clear();
  }
}

class ThemeTimeline extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.cards = [...this.querySelectorAll('[data-results-timeline-card]')];
    this.cards.forEach((card) => card.addEventListener('click', () => this.activate(card, true), { signal }));
    const allowScrollSelection = () => { this.manualSelection = null; };
    this.addEventListener('pointerdown', allowScrollSelection, { signal });
    this.addEventListener('wheel', allowScrollSelection, { passive: true, signal });
    this.addEventListener('keydown', (event) => {
      if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) allowScrollSelection();
    }, { signal });
    const syncAfterScroll = () => {
      this.scrollTimer = null;
      if (!this.closest('product-timeline.product-timeline--scroll')) this.syncActiveFromScroll();
    };
    this.addEventListener('scroll', () => {
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(syncAfterScroll, 120);
    }, { passive: true, signal });
    this.addEventListener('transitionend', (event) => {
      if (event.propertyName === 'flex-basis' && this.cards.includes(event.target)) this.revealSelectedCard();
    }, { signal });
    this.addEventListener('shopify:block:select', (event) => {
      const card = event.target.closest('[data-results-timeline-card]');
      if (this.cards.includes(card)) this.activate(card, true);
    }, { signal });
    if (this.cards.length) this.activate(this.cards.find((card) => card.classList.contains('is-active')) || this.cards[0]);
  }

  syncActiveFromScroll() {
    if (this.manualSelection || !window.matchMedia('(max-width: 749px)').matches) return;
    const rail = this.getBoundingClientRect();
    const mostVisible = this.cards.reduce((best, card) => {
      const bounds = card.getBoundingClientRect();
      const visible = Math.max(0, Math.min(bounds.right, rail.right) - Math.max(bounds.left, rail.left));
      return visible > best.visible ? { card, visible } : best;
    }, { card: null, visible: 0 }).card;
    if (mostVisible && !mostVisible.classList.contains('is-active')) this.activate(mostVisible);
  }

  activate(card, reveal = false) {
    clearTimeout(this.scrollTimer);
    this.scrollTimer = null;
    this.manualSelection = reveal ? card : null;
    cancelAnimationFrame(this.scrollFrame);
    this.cardToReveal = reveal ? card : null;
    this.cards.forEach((item) => {
      const active = item === card;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-pressed', String(active));
    });
    if (reveal) this.scrollFrame = requestAnimationFrame(() => this.revealSelectedCard());
  }

  revealSelectedCard() {
    if (!this.cardToReveal) return;
    const card = this.cardToReveal.getBoundingClientRect();
    const rail = this.getBoundingClientRect();
    const offset = card.left < rail.left ? card.left - rail.left : Math.max(0, card.right - rail.right);
    // Recheck after expansion so the full selected card stays inside the horizontal rail.
    if (Math.abs(offset) > 1) this.scrollBy({ left: offset, behavior: reduceMotion.matches ? 'instant' : 'smooth' });
  }

  disconnectedCallback() {
    this.controller?.abort();
    clearTimeout(this.scrollTimer);
    cancelAnimationFrame(this.scrollFrame);
    this.cardToReveal = null;
    this.manualSelection = null;
  }
}

class ThemeStat extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    cancelAnimationFrame(this.frame);
    this.controller = new AbortController();
    const target = Number(this.dataset.value || 0);
    const finish = () => {
      this.observer?.disconnect();
      cancelAnimationFrame(this.frame);
      this.textContent = `${target}%`;
    };
    reduceMotion.addEventListener('change', () => {
      if (reduceMotion.matches) finish();
    }, { signal: this.controller.signal });
    if (reduceMotion.matches) {
      finish();
      return;
    }
    this.observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      this.observer.disconnect();
      const start = performance.now();
      const draw = (time) => {
        const progress = Math.min(1, (time - start) / 900);
        this.textContent = `${Math.round(target * progress)}%`;
        if (progress < 1) this.frame = requestAnimationFrame(draw);
      };
      this.frame = requestAnimationFrame(draw);
    });
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    cancelAnimationFrame(this.frame);
  }
}

class ThemeCompare extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.labelBounds = null;
    this.range = this.querySelector('input[type="range"]');
    if (!this.range) return;
    this.beforeLabel = this.querySelector('.theme-compare__label--before');
    this.afterLabel = this.querySelector('.theme-compare__label--after');
    const sync = () => {
      this.style.setProperty('--theme-compare', `${this.range.value}%`);
      this.updateLabels();
    };
    this.range.addEventListener('input', sync, { signal });
    this.observer = new ResizeObserver(() => {
      if (!signal.aborted) this.measureLabels();
    });
    [this, this.beforeLabel, this.afterLabel].forEach((element) => {
      if (element) this.observer.observe(element);
    });
    // Initial ResizeObserver delivery measures labels before paint, after layout.
    sync();
  }

  measureLabels() {
    // Read all label geometry together, only when its layout can have changed.
    this.labelBounds = {
      width: this.clientWidth,
      beforeEnd: this.beforeLabel ? this.beforeLabel.offsetLeft + this.beforeLabel.offsetWidth : 0,
      afterStart: this.afterLabel?.offsetLeft ?? 0,
    };
    this.updateLabels();
  }

  updateLabels() {
    // Input before the initial observation still updates the divider immediately.
    if (!this.labelBounds) return;
    const divider = this.labelBounds.width * Number(this.range.value) / 100;
    // Hide the whole badge before it crosses onto the opposite image.
    if (this.beforeLabel) {
      this.beforeLabel.classList.toggle('is-hidden', divider < this.labelBounds.beforeEnd);
    }
    if (this.afterLabel) {
      this.afterLabel.classList.toggle('is-hidden', divider > this.labelBounds.afterStart);
    }
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
  }
}

class ThemeHomeMedia extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.videos = [...this.querySelectorAll('video')];
    if (!this.videos.length) return;
    this.poster = this.querySelector('[data-theme-video-poster]');
    this.posterPainted = !this.poster;
    this.posterStartFrame = null;
    const posterImage = this.poster?.querySelector('img');
    if (posterImage) {
      const posterReady = () => {
        if (this.posterPainted || this.posterStartFrame != null) return;
        this.posterStartFrame = requestAnimationFrame(() => {
          this.posterStartFrame = requestAnimationFrame(() => {
            this.posterStartFrame = null;
            this.posterPainted = true;
            this.sync();
          });
        });
      };
      if (posterImage.complete) posterReady();
      else {
        posterImage.addEventListener('load', posterReady, { once: true, signal });
        posterImage.addEventListener('error', posterReady, { once: true, signal });
      }
    } else this.posterPainted = true;
    this.presentedVideos = new WeakSet();
    this.posterFrames = new Map();
    this.playButton = this.querySelector('[data-theme-video-playback]');
    this.soundButton = this.querySelector('[data-theme-video-sound]');
    this.playButton?.addEventListener('click', () => this.togglePlayback(), { signal });
    this.soundButton?.addEventListener('click', () => this.toggleSound(), { signal });
    this.videos.forEach((video) => {
      const primarySource = video.querySelector('source[src]:not([data-theme-video-fallback-src])');
      const fallbackSources = [...video.querySelectorAll('source[data-theme-video-fallback-src]')];
      if (primarySource && fallbackSources.length) {
        primarySource.addEventListener('error', () => {
          primarySource.remove();
          fallbackSources.forEach((source) => {
            source.setAttribute('src', source.getAttribute('data-theme-video-fallback-src'));
          });
          video.load();
          this.sync();
        }, { once: true, signal });
      }
      ['loadedmetadata', 'loadeddata', 'playing', 'play', 'pause', 'volumechange', 'emptied', 'error'].forEach((event) => {
        video.addEventListener(event, () => this.updateControls(), { signal });
      });
      // Some browsers expose audio only after decoding begins. Keep probing until
      // it appears, without geometry reads or repeated control writes on progress.
      video.addEventListener('timeupdate', () => {
        if (video === this.currentVideo && this.soundButton?.hidden && this.hasAudio(video)) this.updateControls();
      }, { signal });
      video.audioTracks?.addEventListener('addtrack', () => this.updateControls(), { signal });
      video.audioTracks?.addEventListener('removetrack', () => this.updateControls(), { signal });
    });
    const sync = () => this.sync();
    this.mobile = window.matchMedia('(max-width: 749px)');
    this.mobile.addEventListener('change', sync, { signal });
    reduceMotion.addEventListener('change', sync, { signal });
    document.addEventListener('visibilitychange', sync, { signal });
    this.closest('theme-carousel, theme-tabs')?.addEventListener('theme:slide-change', sync, { signal });
    const productCard = this.closest('.theme-product-card-adapter')?.querySelector(':scope > product-card');
    for (const event of ['pointerenter', 'pointerleave', 'focusin', 'focusout']) {
      productCard?.addEventListener(event, () => requestAnimationFrame(sync), { signal });
    }
    this.observer?.disconnect();
    this.intersecting = undefined;
    this.observer = new IntersectionObserver((entries) => {
      if (signal.aborted) return;
      // This observer owns one media element. A delivery may contain several
      // queued crossings; the latest entry describes its current visibility.
      const entry = entries.at(-1);
      const intersecting = entry.isIntersecting && entry.intersectionRatio > 0;
      // The first delivery resolves responsive selection after layout, even
      // offscreen. Later unchanged crossings need no geometry or media writes.
      if (this.intersecting === intersecting) return;
      this.intersecting = intersecting;
      this.sync();
    }, { threshold: [0, 0.01] });
    this.observer.observe(this);
  }

  sync() {
    const hoverLayer = this.closest('.theme-product-card-adapter')?.querySelector(':scope > product-card > product-card-hover');
    const hoverStyle = hoverLayer && getComputedStyle(hoverLayer);
    const coveredByHover = hoverStyle && hoverStyle.display !== 'none' && hoverStyle.opacity === '1';
    // Snapshot responsive selection and visibility before playback/mute writes.
    const measurements = this.videos.map((video) => {
      const rect = video.getBoundingClientRect();
      const selected = rect.width > 0 && rect.height > 0 && !video.closest('[hidden], [aria-hidden="true"], [inert]');
      const visible = this.intersecting && selected && rect.bottom > 0 && rect.top < window.innerHeight && rect.right > 0 && rect.left < window.innerWidth;
      return { video, selected, visible };
    });
    this.currentVideo = measurements.find(({ selected }) => selected)?.video;
    const paused = this.userPaused || reduceMotion.matches || document.hidden || this.closest('[aria-hidden="true"]') || coveredByHover;
    measurements.forEach(({ video, visible }) => {
      if (paused || !visible) {
        if (!video.paused) video.pause();
        if ((!visible || document.hidden) && !video.muted) video.muted = true;
      } else if (video.paused && this.posterPainted) {
        video.play()?.catch(() => {});
      }
    });
    this.updateControls();
    this.dispatchEvent(new CustomEvent('theme:media-change', { bubbles: true }));
  }

  activeVideo() {
    return this.currentVideo;
  }

  hasAudio(video) {
    if (video.closest('[data-video-has-sound]')?.dataset.videoHasSound === 'false') return false;
    if (video.srcObject?.getAudioTracks) return video.srcObject.getAudioTracks().length > 0;
    if (video.audioTracks?.length > 0) return true;
    if (typeof video.mozHasAudio === 'boolean') return video.mozHasAudio;
    return video.webkitAudioDecodedByteCount > 0;
  }

  togglePlayback() {
    const video = this.activeVideo();
    if (!video) return;
    this.userPaused = !video.paused;
    if (this.userPaused) video.pause();
    else {
      this.posterPainted = true;
      video.play()?.catch(() => this.updateControls());
    }
    this.updateControls();
  }

  toggleSound() {
    const video = this.activeVideo();
    if (!video || !this.hasAudio(video)) return;
    video.muted = !video.muted;
    this.videos.filter((other) => other !== video).forEach((other) => { other.muted = true; });
    this.updateControls();
  }

  posterFrameReady(video) {
    if (!video) return false;
    if (video.readyState < 2 || video.error) {
      this.presentedVideos.delete(video);
      this.posterFrames.get(video)?.();
      this.posterFrames.delete(video);
      return false;
    }
    if (this.presentedVideos.has(video)) return true;
    if (this.posterFrames.has(video)) return false;

    const presented = () => {
      this.posterFrames.delete(video);
      if (!this.isConnected || video.readyState < 2 || video.error) return;
      if (typeof video.requestVideoFrameCallback !== 'function' && video.paused) return;
      this.presentedVideos.add(video);
      this.updateControls();
    };
    if (typeof video.requestVideoFrameCallback === 'function') {
      const id = video.requestVideoFrameCallback(presented);
      this.posterFrames.set(video, () => video.cancelVideoFrameCallback?.(id));
    } else if (!video.paused) {
      let id = requestAnimationFrame(() => { id = requestAnimationFrame(presented); });
      this.posterFrames.set(video, () => cancelAnimationFrame(id));
    }
    return false;
  }

  updateControls() {
    const video = this.activeVideo();
    const descriptionId = video?.getAttribute('aria-describedby');
    if (this.poster) {
      const frameReady = this.posterFrameReady(video);
      if (this.poster.hidden !== frameReady) this.poster.hidden = frameReady;
    }
    const setAttribute = (element, name, value) => {
      if (element.getAttribute(name) === (value ?? null)) return;
      if (value == null) element.removeAttribute(name);
      else element.setAttribute(name, value);
    };
    const updateButton = (button, hidden, state, label) => {
      if (!button) return;
      if (button.hidden !== hidden) button.hidden = hidden;
      setAttribute(button, 'aria-describedby', descriptionId);
      if (!video) return;
      setAttribute(button, 'data-state', state);
      setAttribute(button, 'aria-label', label);
    };
    updateButton(this.playButton, !video, video?.paused ? 'paused' : 'playing',
      video?.paused ? this.playButton?.dataset.playLabel : this.playButton?.dataset.pauseLabel);
    updateButton(this.soundButton, !video || !this.hasAudio(video), video?.muted ? 'muted' : 'audible',
      video?.muted ? this.soundButton?.dataset.unmuteLabel : this.soundButton?.dataset.muteLabel);
  }

  disconnectedCallback() {
    this.controller?.abort();
    if (this.posterStartFrame != null) cancelAnimationFrame(this.posterStartFrame);
    for (const cancel of this.posterFrames?.values() ?? []) cancel();
    this.posterFrames?.clear();
    this.observer?.disconnect();
    this.videos?.forEach((video) => video.pause());
  }
}

const register = (name, constructor) => {
  if (!customElements.get(name)) customElements.define(name, constructor);
};

register('theme-carousel', ThemeCarousel);
register('theme-tabs', ThemeTabs);
register('theme-accordion', ThemeAccordion);
register('results-timeline', ThemeTimeline);
register('theme-stat', ThemeStat);
register('theme-compare', ThemeCompare);
register('responsive-section-media', ThemeHomeMedia);

const observeMotion = (root = document) => {
  if (reduceMotion.matches) return;
  const nodes = root.querySelectorAll('.theme-motion:not(.is-visible)');
  if (!nodes.length) return;
  // Use the browser's intersection snapshot. Reading every section's geometry
  // here forces offscreen content to render during initialisation.
  const reveal = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        reveal.unobserve(entry.target);
      } else {
        entry.target.classList.add('theme-motion--ready');
      }
    });
  }, { threshold: 0.12 });
  nodes.forEach((node) => reveal.observe(node));
};

observeMotion();
document.addEventListener('shopify:section:load', (event) => observeMotion(event.target));
